/**
 * Pricing configuration for different regions and currencies
 * All base prices are in INR, converted to local currencies
 */

export type CurrencyCode = 'INR' | 'USD' | 'EUR' | 'GBP' | 'MYR' | 'SGD' | 'AUD' | 'CAD' | 'AED';

export interface CurrencyConfig {
	code: CurrencyCode;
	symbol: string;
	locale: string;
	rate: number; // Exchange rate relative to INR (1 INR = X currency)
	countries: string[]; // ISO country codes that use this currency
}

export const currencies: Record<CurrencyCode, CurrencyConfig> = {
	INR: {
		code: 'INR',
		symbol: '₹',
		locale: 'en-IN',
		rate: 1,
		countries: ['IN', 'NP', 'BD', 'LK', 'PK']
	},
	USD: {
		code: 'USD',
		symbol: '$',
		locale: 'en-US',
		rate: 0.012, // 1 INR ≈ 0.012 USD
		countries: ['US', 'CA', 'MX', 'PH', 'VN', 'TH', 'ID']
	},
	EUR: {
		code: 'EUR',
		symbol: '€',
		locale: 'de-DE',
		rate: 0.011, // 1 INR ≈ 0.011 EUR
		countries: ['DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'AT', 'PT', 'IE', 'GR', 'FI']
	},
	GBP: {
		code: 'GBP',
		symbol: '£',
		locale: 'en-GB',
		rate: 0.0095, // 1 INR ≈ 0.0095 GBP
		countries: ['GB', 'JE', 'GG', 'IM']
	},
	MYR: {
		code: 'MYR',
		symbol: 'RM',
		locale: 'ms-MY',
		rate: 0.056, // 1 INR ≈ 0.056 MYR
		countries: ['MY']
	},
	SGD: {
		code: 'SGD',
		symbol: 'S$',
		locale: 'en-SG',
		rate: 0.016, // 1 INR ≈ 0.016 SGD
		countries: ['SG']
	},
	AUD: {
		code: 'AUD',
		symbol: 'A$',
		locale: 'en-AU',
		rate: 0.018, // 1 INR ≈ 0.018 AUD
		countries: ['AU', 'NZ', 'PG', 'FJ']
	},
	CAD: {
		code: 'CAD',
		symbol: 'C$',
		locale: 'en-CA',
		rate: 0.016, // 1 INR ≈ 0.016 CAD
		countries: ['CA']
	},
	AED: {
		code: 'AED',
		symbol: 'د.إ',
		locale: 'ar-AE',
		rate: 0.044, // 1 INR ≈ 0.044 AED
		countries: ['AE', 'SA', 'QA', 'KW', 'BH', 'OM']
	}
};

// Base prices in INR
export const basePrices = {
	starter: 39900,
	standard: 89900
};

// Masterclass prices for each currency (fixed prices, not calculated from exchange rates)
export const masterclassPrices: Record<CurrencyCode, number> = {
	INR: 499,
	MYR: 39,
	SGD: 24,
	USD: 20,
	EUR: 18,
	GBP: 16,
	AUD: 30,
	CAD: 27,
	AED: 75
};

/**
 * Detect user's currency from browser locale and timezone
 * Timezone is prioritized over locale for better accuracy
 * Added UTC offset fallback for better Malaysia detection
 */
export function detectCurrency(): CurrencyCode {
	if (typeof window === 'undefined') return 'INR';

	// Check localStorage for saved preference, but verify against current location
	const saved = localStorage.getItem('preferred-currency') as CurrencyCode;
	if (saved && currencies[saved]) {

		// Verify saved currency against current timezone
		try {
			const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
			const timezoneCurrencyMap: Record<string, CurrencyCode> = {
				'Asia/Kuala_Lumpur': 'MYR',
				'Asia/Kuching': 'MYR',
				'Asia/Johor_Bahru': 'MYR',
				'Asia/Pontianak': 'MYR',
				'Asia/Singapore': 'SGD',
				'Asia/Manila': 'USD',
				'Asia/Jakarta': 'USD',
				'Asia/Bangkok': 'USD',
				'Asia/Shanghai': 'USD',
				'Asia/Hong_Kong': 'USD',
				'Asia/Kolkata': 'INR',
				'Asia/Calcutta': 'INR',
				'America/New_York': 'USD',
				'America/Chicago': 'USD',
				'America/Denver': 'USD',
				'America/Los_Angeles': 'USD',
				'America/Phoenix': 'USD',
				'America/Anchorage': 'USD',
				'Europe/London': 'GBP',
				'Europe/Dublin': 'GBP',
				'Europe/Paris': 'EUR',
				'Europe/Berlin': 'EUR',
				'Europe/Rome': 'EUR',
				'Europe/Madrid': 'EUR',
				'Europe/Amsterdam': 'EUR',
				'Europe/Brussels': 'EUR',
				'Europe/Vienna': 'EUR',
				'Australia/Sydney': 'AUD',
				'Australia/Melbourne': 'AUD',
				'Australia/Brisbane': 'AUD',
				'Australia/Perth': 'AUD',
				'America/Toronto': 'CAD',
				'America/Vancouver': 'CAD',
				'America/Montreal': 'CAD',
				'America/Edmonton': 'CAD',
				'Asia/Dubai': 'AED',
				'Asia/Abu_Dhabi': 'AED',
				'Asia/Riyadh': 'AED',
				'Asia/Doha': 'AED'
			};

			// If timezone maps to a different currency, update the saved preference
			if (timezone && timezoneCurrencyMap[timezone] && timezoneCurrencyMap[timezone] !== saved) {
				const detected = timezoneCurrencyMap[timezone];
				localStorage.setItem('preferred-currency', detected);
				return detected;
			}

			return saved;
		} catch (e) {
			return saved;
		}
	}

	// Method 1: Try timezone first (more accurate for physical location)
	try {
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

		// Map of timezones to currencies
		const timezoneCurrencyMap: Record<string, CurrencyCode> = {
			// Malaysia timezone (UTC+8)
			'Asia/Kuala_Lumpur': 'MYR',
			'Asia/Kuching': 'MYR',
			'Asia/Johor_Bahru': 'MYR',
			'Asia/Pontianak': 'MYR',

			// Singapore
			'Asia/Singapore': 'SGD',

			// Philippines (UTC+8) - mapped to USD
			'Asia/Manila': 'USD',

			// Indonesia (UTC+7) - mapped to USD
			'Asia/Jakarta': 'USD',

			// Thailand (UTC+7) - mapped to USD
			'Asia/Bangkok': 'USD',

			// China (UTC+8) - mapped to USD
			'Asia/Shanghai': 'USD',
			'Asia/Hong_Kong': 'USD',

			// India
			'Asia/Kolkata': 'INR',
			'Asia/Calcutta': 'INR',

			// US (default to USD)
			'America/New_York': 'USD',
			'America/Chicago': 'USD',
			'America/Denver': 'USD',
			'America/Los_Angeles': 'USD',
			'America/Phoenix': 'USD',
			'America/Anchorage': 'USD',

			// Europe
			'Europe/London': 'GBP',
			'Europe/Dublin': 'GBP',
			'Europe/Paris': 'EUR',
			'Europe/Berlin': 'EUR',
			'Europe/Rome': 'EUR',
			'Europe/Madrid': 'EUR',
			'Europe/Amsterdam': 'EUR',
			'Europe/Brussels': 'EUR',
			'Europe/Vienna': 'EUR',

			// Australia
			'Australia/Sydney': 'AUD',
			'Australia/Melbourne': 'AUD',
			'Australia/Brisbane': 'AUD',
			'Australia/Perth': 'AUD',

			// Canada
			'America/Toronto': 'CAD',
			'America/Vancouver': 'CAD',
			'America/Montreal': 'CAD',
			'America/Edmonton': 'CAD',

			// Middle East
			'Asia/Dubai': 'AED',
			'Asia/Abu_Dhabi': 'AED',
			'Asia/Riyadh': 'AED',
			'Asia/Doha': 'AED'
		};

		if (timezone && timezoneCurrencyMap[timezone]) {
			return timezoneCurrencyMap[timezone];
		}
	} catch (e) {
		// Timezone detection failed, continue to locale
	}

	// Method 1.5: Try UTC offset as fallback for better detection
	try {
		const offset = -(new Date().getTimezoneOffset()); // in minutes
		const offsetHours = offset / 60;

		// For UTC+8 (Malaysia, Singapore), check locale to distinguish
		if (offsetHours === 8) {
			// First, try to detect from timezone string if available
			try {
				const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
				if (tz && tz.toLowerCase().includes('singapore')) {
					return 'SGD';
				}
			} catch {}

			// Check browser locale for more specificity
			const locale = navigator.language || '';
			// Singapore locale detection (en-SG, zh-SG, ms-SG, ta-SG)
			if (locale.includes('SG') || locale.includes('sg')) {
				return 'SGD';
			}

			// Default to MYR for other UTC+8 regions (Malaysia, Philippines, etc.)
			return 'MYR';
		}
	} catch (e) {
	}

	// Method 2: Try browser locale as fallback
	const locale = navigator.language || 'en-IN';
	const countryCode = locale.split('-')[1]?.toUpperCase();

	if (countryCode) {
		// Find matching currency for this country
		for (const [code, config] of Object.entries(currencies)) {
			if (config.countries.includes(countryCode)) {
				return code as CurrencyCode;
			}
		}
	}

	// Default to INR
	return 'INR';
}

/**
 * Convert INR price to target currency
 */
export function convertPrice(inrPrice: number, targetCurrency: CurrencyCode): number {
	const currency = currencies[targetCurrency];
	return inrPrice * currency.rate;
}

/**
 * Format price for display
 */
export function formatPrice(price: number, currency: CurrencyCode): string {
	const config = currencies[currency];

	// Round to nice numbers based on price range
	let displayPrice: number;
	if (currency === 'INR') {
		// For INR, round to nearest 1000 for prices above 10,000
		if (price >= 10000) {
			displayPrice = Math.round(price / 1000) * 1000;
		} else if (price >= 1000) {
			displayPrice = Math.round(price / 100) * 100;
		} else {
			// For prices under 1000, don't round
			displayPrice = Math.round(price);
		}
	} else {
		// For other currencies, use minimum rounding to preserve small prices
		if (price >= 1000) {
			displayPrice = Math.round(price / 100) * 100;
		} else if (price >= 100) {
			displayPrice = Math.round(price / 10) * 10;
		} else {
			// For prices under 100, round to nearest whole number
			displayPrice = Math.round(price);
		}
		// Ensure we never show 0 for a positive price
		if (displayPrice === 0 && price > 0) {
			displayPrice = Math.ceil(price);
		}
	}

	// Format number without currency symbol
	const formattedNumber = new Intl.NumberFormat(config.locale, {
		minimumFractionDigits: 0,
		maximumFractionDigits: 0
	}).format(displayPrice);

	// Add currency code after the number
	return `${config.symbol}${formattedNumber}`;
}

/**
 * Get pricing display string
 */
export function getPricing(inrPrice: number, currency?: CurrencyCode): string {
	const targetCurrency = currency || detectCurrency();
	const convertedPrice = convertPrice(inrPrice, targetCurrency);
	return formatPrice(convertedPrice, targetCurrency);
}

/**
 * Get masterclass pricing (uses fixed prices per currency, not exchange rate conversion)
 */
export function getMasterclassPricing(currency?: CurrencyCode): string {
	const targetCurrency = currency || detectCurrency();
	const price = masterclassPrices[targetCurrency] || masterclassPrices.INR;
	return formatPrice(price, targetCurrency);
}

/**
 * Save user's currency preference
 */
export function saveCurrencyPreference(currency: CurrencyCode): void {
	if (typeof window !== 'undefined') {
		localStorage.setItem('preferred-currency', currency);
	}
}
